import { CloudUploadOutlined, Info, PersonRemove } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FilePicker } from 'react-file-picker';

// MESSAGES
import ContactImporterMessages from '../../messages/ContactImporterFormattedMessages';

// REQUEST
import useContactImporterRequest from '../../requests/ContactImporterRequest';

const ContactImporter = (props) => {
  const [contactType, setContactType] = useState('Neither');
  const [errorText, setErrorText] = useState('');
  const [failureModal, setFailureModal] = useState(false);
  const [errorFilename, setErrorFilename] = useState(undefined);
  const [contactsToImport, setContactsToImport] = useState(null);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const { importContactsCsv, importContacts, exportFailedContacts } =
    useContactImporterRequest();

  useEffect(() => {
    if (props.importType) {
      setContactType(props.importType);
    }
  }, [props.importType]);

  const generateTypeSelect = () => {
    return (
      <FormControl component="fieldset">
        <Stack
          sx={{
            flexDirection: { md: 'row' },
            alignItems: { md: 'center' },
            gap: { md: 2 },
          }}
        >
          <FormLabel sx={{ minWidth: 150 }} required>
            {ContactImporterMessages.contactType}
          </FormLabel>

          <RadioGroup
            row
            aria-label="contactType"
            name="contactType"
            onChange={handleForm}
          >
            <FormControlLabel
              value="Personal"
              control={<Radio color="secondary" />}
              label={ContactImporterMessages.personal}
              checked={contactType === 'Personal'}
            />
            <FormControlLabel
              value="Global"
              control={<Radio color="secondary" />}
              label={ContactImporterMessages.global}
              checked={contactType === 'Global'}
              disabled={
                props.accessLevel > '0' && props.accessType !== '0'
                  ? false
                  : true
              }
            />
          </RadioGroup>
        </Stack>
      </FormControl>
    );
  };

  const captureFile = (fileObject) => {
    setFile(fileObject);
    setErrorMessage('');
    setErrorText('');
  };

  const generateFileSelect = () => {
    return (
      <Box>
        <Stack
          sx={{
            flexDirection: { md: 'row' },
            alignItems: { md: 'center' },
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 160 }}>
            <FilePicker
              extensions={['csv']}
              onChange={(fileObject) => captureFile(fileObject)}
              onError={(errMsg) => setErrorMessage(errMsg)}
            >
              <Button
                disabled={contactType === 'Neither'}
                color="secondary"
                variant="outlined"
                startIcon={<CloudUploadOutlined />}
              >
                Browse
              </Button>
            </FilePicker>
          </Box>

          <TextField
            className="default-cursor"
            type="text"
            placeholder={file ? `${file.name}` : 'Select File'}
            disabled
            fullWidth
            size="small"
          />
        </Stack>

        {generateFileSelectError()}

        <div className="marginTopMedium">
          {'Please Note: The CSV Format including headers should match '}
          <Link href="/static/example_contact_import.csv" download>
            this file
          </Link>
          .
        </div>
      </Box>
    );
  };

  const generateFileSelectError = () => {
    if (!errorMessage) return null;
    return <Alert severity="error">{errorMessage}</Alert>;
  };

  const uploadContacts = async () => {
    try {
      if (!file || typeof file === 'undefined') {
        return setErrorText('Must select a valid CSV file to upload');
      }

      setImporting(true);

      const response = await importContactsCsv(
        contactType,
        file,
        props.distributionListId,
        props.errorHandler
      );

      setImporting(false);

      if (!response) return;

      setContactsToImport(response.contactsToAdd);
      props.setShowContactImportModalSize(800);

      if (response.errorFilename) {
        setFailureModal(true);
        setErrorFilename(response.errorFilename);
      }
    } catch (e) {
      console.log('Got upload err:', e);
      setErrorText(e);
      setImporting(false);
    }
  };

  const saveContacts = async () => {
    if (!contactsToImport) return;

    setSaving(true);

    const validContactsToImport = contactsToImport.filter(
      (contact) => contact.status === 'VALID'
    );

    const newContacts = await importContacts(
      validContactsToImport,
      props.errorHandler
    );

    if (newContacts) {
      if (props.setNewContacts) props.setNewContacts(newContacts);
      props.handleBackClick();
    }

    setSaving(false);
  };

  const generateSubmitButton = () => {
    const buttonText = importing
      ? 'Importing'
      : ContactImporterMessages.importText;
    return (
      <Stack sx={{ marginTop: 2, justifyContent: 'end', alignItems: 'end' }}>
        <Button
          color="secondary"
          variant="contained"
          onClick={uploadContacts}
          disabled={!file || importing}
        >
          {buttonText}
        </Button>
      </Stack>
    );
  };

  const generateError = () => {
    if (!errorText) return null;
    return <div className="text-danger">{errorText}</div>;
  };

  const handleForm = (event) => {
    const { value, name } = event.target;
    if (name === 'contactType') {
      setContactType(value);
    }
  };

  const handleModalClose = () => {
    setFailureModal(false);
    if (props.showContactImporter) props.showContactImporter();
  };

  const handleDownload = () => {
    if (errorFilename) {
      exportFailedContacts(errorFilename, props.errorHandler);
    }
    if (props.showContactImporter) props.showContactImporter();
  };

  const selectRowsToBeDeleted = (index) => {
    if (!contactsToImport) return;
    setContactsToImport(contactsToImport.toSpliced(index, 1));
  };

  const generateTable = () => {
    if (!contactsToImport || !contactsToImport.length) return null;

    const hasErrors = contactsToImport.some(
      (contact) => contact.status !== 'VALID'
    );

    const rows = contactsToImport.map((smsContact, i) => {
      const {
        contact_name,
        status,
        statusInfo,
        label,
        contact_email,
        display_tn,
        contact_tn,
        contact_type,
      } = smsContact;
      const textClass = status != 'VALID' ? 'red' : 'inherit';
      const tn = display_tn ? '+' + display_tn : contact_tn;
      return (
        <TableRow key={i}>
          <TableCell sx={{ color: textClass }}>
            {contact_name || label}
          </TableCell>
          <TableCell sx={{ color: textClass }}>{contact_email}</TableCell>
          <TableCell sx={{ color: textClass }}>{tn}</TableCell>
          <TableCell sx={{ color: textClass }}>
            {contact_type === 'global' ? 'Global' : 'Personal'}
          </TableCell>
          <TableCell sx={{ color: textClass }}>
            {status === 'DUPLICATE' ? (
              <Tooltip
                title={statusInfo}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  my: 2,
                }}
              >
                DUPLICATE
                <Info sx={{ ml: 1, fontSize: 16 }} />
              </Tooltip>
            ) : (
              status
            )}
          </TableCell>
          <TableCell>
            <IconButton onClick={() => selectRowsToBeDeleted(i)}>
              <PersonRemove color="error" />
            </IconButton>
          </TableCell>
        </TableRow>
      );
    });

    return (
      <Box>
        <TableContainer sx={{ marginBottom: 2 }}>
          <Table variant="rounded">
            <TableHead>
              <TableRow key="head">
                <TableCell>{'Name'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {ContactImporterMessages.emailText}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {ContactImporterMessages.mobileText}
                </TableCell>
                <TableCell>{'Type'}</TableCell>
                <TableCell>{'Validation'}</TableCell>
                <TableCell>{'Remove'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{rows}</TableBody>
          </Table>
        </TableContainer>

        <Stack sx={{ flexDirection: 'row', gap: 2, justifyContent: 'end' }}>
          <Button
            onClick={props.handleBackClick}
            color="secondary"
            variant="link"
          >
            Cancel
          </Button>

          <Button
            onClick={saveContacts}
            disabled={saving || hasErrors}
            color="secondary"
            variant="contained"
          >
            {saving ? 'Saving' : 'Save Contacts'}
          </Button>
        </Stack>
      </Box>
    );
  };

  return (
    <Box>
      {failureModal ? (
        <Box>
          <Typography variant="h3" gutterBottom>
            {ContactImporterMessages.modalWarning}
          </Typography>

          <Typography gutterBottom>
            One or more contacts failed to be added. The failed contacts can be
            downloaded below. The other rows were added successfully.
          </Typography>

          <Stack sx={{ flexDirection: 'row', justifyContent: 'end', gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleModalClose}
            >
              Ignore
            </Button>

            <Button
              color="secondary"
              variant="contained"
              onClick={handleDownload}
            >
              Download
            </Button>
          </Stack>
        </Box>
      ) : contactsToImport ? (
        <>{generateTable()}</>
      ) : (
        <>
          {generateTypeSelect()}
          {generateFileSelect()}
          {generateError()}
          {generateSubmitButton()}
        </>
      )}
    </Box>
  );
};

export default ContactImporter;
